import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AssistantAPIClient from '../../api/assistant';
import { ChatMessage, Clip } from '../../components/assistant/types';
import { useToast } from '../components/ui/Toast';
import { useOrg } from './OrgProvider';
import { useAuth } from './AuthProvider';
import { getMessageAudio } from '../../api/server/messages';
import { logger } from '../../utils/logger';
import { Analytics, AnalyticsEvent } from '../utils/analytics';

export interface KodiMessage {
  id: string;
  query: string;
  response: {
    summary: string;
    clips?: Clip[];
    report?: string;
    isThinking?: boolean;
  };
  timestamp: Date;
}

interface KodiContextValue {
  messages: KodiMessage[];
  isLoading: boolean;
  isProcessing: boolean; // True when a request is in-flight (even in background)
  hasUnreadResponse: boolean; // True when response came in while user was away
  query: string;
  setQuery: (query: string) => void;
  sendQuery: (queryText?: string) => Promise<void>;
  clearMessages: () => void;
  playingClipId: string | null;
  handlePlayPause: (clip: Clip) => void;
}

const KodiContext = createContext<KodiContextValue | null>(null);

export const useKodi = () => {
  const context = useContext(KodiContext);
  if (!context) {
    throw new Error('useKodi must be used within a KodiProvider');
  }
  return context;
};

interface KodiProviderProps {
  children: React.ReactNode;
}

export const KodiProvider: React.FC<KodiProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast, removeToast } = useToast();

  // V2 org state (from OrgProvider)
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.org_id;

  // User state (from AuthProvider) - for feedback tracking
  const { user } = useAuth();
  const userId = user?.id;

  const [messages, setMessages] = useState<KodiMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasUnreadResponse, setHasUnreadResponse] = useState(false);
  const [query, setQuery] = useState('');
  const [context, setContext] = useState<any>(null);
  const [playingClipId, setPlayingClipId] = useState<string | null>(null);
  const playingClipIdRef = useRef<string | null>(null);
  const currentBlobUrlRef = useRef<string | null>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  // Track mounted state to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Track current path in ref so async callbacks get current value
  const currentPathRef = useRef(location.pathname);

  // Keep path ref updated when location changes
  // Clear unread indicator when user navigates to assistant page
  useEffect(() => {
    currentPathRef.current = location.pathname;
    if (location.pathname.includes('/assistant') && hasUnreadResponse) {
      setHasUnreadResponse(false);
    }
  }, [location.pathname, hasUnreadResponse]);

  // Track previous org_id to detect changes
  const prevOrgIdRef = useRef<string | null>(null);

  // Initialize/reset context when orgId changes
  useEffect(() => {
    if (orgId) {
      // If org changed, clear messages and reset context
      if (prevOrgIdRef.current && prevOrgIdRef.current !== orgId) {
        logger.info('[KodiProvider] Organization changed, clearing conversation');
        setMessages([]);
        setHasUnreadResponse(false);
      }

      setContext({
        org_id: orgId,
        user_id: userId, // Include user ID for feedback tracking
        dialog_history: [],
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });

      prevOrgIdRef.current = orgId;
    }
  }, [orgId, userId]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      if (currentBlobUrlRef.current) {
        URL.revokeObjectURL(currentBlobUrlRef.current);
        currentBlobUrlRef.current = null;
      }
    };
  }, []);

  /** Stop current audio and revoke its blob URL */
  const stopCurrentAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.removeAttribute('src');
      currentAudioRef.current.load();
      currentAudioRef.current = null;
    }
    if (currentBlobUrlRef.current) {
      URL.revokeObjectURL(currentBlobUrlRef.current);
      currentBlobUrlRef.current = null;
    }
    playingClipIdRef.current = null;
    setPlayingClipId(null);
  }, []);

  const handlePlayPause = useCallback(
    async (clip: Clip) => {
      const clipId = clip.talk_id || clip.id;

      // If this clip is currently playing, pause it
      if (playingClipIdRef.current === clipId && currentAudioRef.current) {
        currentAudioRef.current.pause();
        playingClipIdRef.current = null;
        setPlayingClipId(null);
        return;
      }

      // Stop any currently playing audio and clean up
      stopCurrentAudio();

      if (!clipId) {
        logger.error('[KodiProvider] No clip ID for audio playback');
        return;
      }

      // Fetch audio blob via the standard messages API (handles auth + proxying)
      try {
        const audioBlob = await getMessageAudio(clipId, orgId);
        const blobUrl = URL.createObjectURL(audioBlob);

        const audio = new Audio(blobUrl);
        currentBlobUrlRef.current = blobUrl;

        const cleanup = () => {
          if (currentBlobUrlRef.current === blobUrl) {
            URL.revokeObjectURL(blobUrl);
            currentBlobUrlRef.current = null;
          }
          playingClipIdRef.current = null;
          setPlayingClipId(null);
          currentAudioRef.current = null;
        };

        audio.addEventListener('ended', cleanup);
        audio.addEventListener('error', (err: Event) => {
          logger.error('[KodiProvider] Audio playback error:', err);
          cleanup();
        });

        await audio.play();

        // Set playing state only after playback actually starts
        currentAudioRef.current = audio;
        playingClipIdRef.current = clipId;
        setPlayingClipId(clipId);
      } catch (err) {
        logger.error('[KodiProvider] Failed to play audio:', err);
        stopCurrentAudio();
      }
    },
    [orgId, stopCurrentAudio]
  );

  const sendQuery = useCallback(
    async (queryText?: string) => {
      const userQuery = (queryText || query).trim();
      if (!userQuery || !context) return;

      setQuery(''); // Clear input immediately
      setIsLoading(true);
      setIsProcessing(true);

      // Create a placeholder message with "thinking" state
      const messageId = Date.now().toString();
      const placeholderMessage: KodiMessage = {
        id: messageId,
        query: userQuery,
        response: {
          summary: '',
          isThinking: true
        },
        timestamp: new Date()
      };

      // Add user message + thinking placeholder immediately
      setMessages((prev) => [...prev, placeholderMessage]);

      try {
        // Update context with current chat history (exclude the placeholder)
        const chatHistory: ChatMessage[] = messages
          .map((msg) => [
            { role: 'user' as const, content: msg.query },
            { role: 'assistant' as const, content: msg.response.summary }
          ])
          .flat();

        const updatedContext = {
          ...context,
          dialog_history: chatHistory
        };

        // Call the real API
        logger.info('[KodiProvider] Sending query:', { query: userQuery, context: updatedContext });
        Analytics.track(AnalyticsEvent.KODI_QUERY_SENT, {
          query_length: userQuery.length,
          has_context: !!context
        });
        const response = await AssistantAPIClient.sendQuery(userQuery, updatedContext);

        logger.info('[KodiProvider] Received response:', response);
        const hasClips =
          'response' in response
            ? Boolean(response.clips?.length)
            : Boolean(
                response.result?.content?.messages?.length || response.result?.messages?.length
              );
        const hasReport =
          'response' in response
            ? false
            : Boolean(response.result?.report || response.result?.content?.report);
        Analytics.track(AnalyticsEvent.KODI_RESPONSE_RECEIVED, {
          has_clips: hasClips,
          has_report: hasReport
        });

        // Parse the response
        let summary = '';
        let clips: Clip[] = [];
        let report = '';

        if ('response' in response) {
          // Enhanced API response (Kodi / Agency format)
          summary = response.response;
          clips = response.messages || response.clips || [];
          // Only update context if the response includes one (Kodi doesn't return context)
          if (response.context) {
            setContext(response.context);
          }
        } else {
          // Legacy NLU response
          const result = response.result;

          // Check if this is a report response
          if (result.report) {
            report = result.report;
            summary = result.utterance || 'Here is the report you requested.';
          } else if (result.content && result.content.report) {
            report = result.content.report;
            summary = result.utterance || 'Here is the report you requested.';
          } else {
            // Extract text response
            if (result.utterance) {
              summary = result.utterance;
            } else if (result.response) {
              summary = result.response;
            } else if (result.text) {
              summary = result.text;
            } else {
              summary = 'I received your query but could not generate a response.';
            }

            // Extract clips if available
            if (
              result.content &&
              result.content.messages &&
              Array.isArray(result.content.messages)
            ) {
              clips = result.content.messages;
            } else if (result.messages && Array.isArray(result.messages)) {
              clips = result.messages;
            } else if (result.clips && Array.isArray(result.clips)) {
              clips = result.clips;
            }
          }

          if (response.context) {
            setContext(response.context);
          }
        }

        // Update the placeholder message with real response
        const completedMessage: KodiMessage = {
          id: messageId,
          query: userQuery,
          response: {
            summary,
            clips: clips.length > 0 ? clips : undefined,
            report: report || undefined,
            isThinking: false
          },
          timestamp: new Date()
        };

        // Replace the placeholder message with the completed one (only if still mounted)
        if (!isMountedRef.current) return;

        setMessages((prevMessages) =>
          prevMessages.map((msg) => (msg.id === messageId ? completedMessage : msg))
        );

        // Check current path (using ref for current value, not stale closure)
        const isOnAssistantPage = currentPathRef.current.includes('/assistant');

        // Show toast and set unread if user is not on the assistant page
        if (!isOnAssistantPage) {
          setHasUnreadResponse(true);
          const toastId = addToast({
            type: 'kodi',
            title: 'KODI replied',
            message: summary.length > 100 ? summary.substring(0, 100) + '...' : summary,
            duration: 8000,
            onClick: () => {
              removeToast(toastId);
              navigate('/v2/assistant');
            }
          });
        }
      } catch (error) {
        logger.error('[KodiProvider] Query failed:', error);

        // Determine user-friendly error message
        let errorText = 'Sorry, I encountered an error processing your request.';

        if (error instanceof Error) {
          if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
            errorText = 'Unable to connect to the AI service. Please check your connection.';
          } else if (error.message.includes('401') || error.message.includes('Invalid API Key')) {
            errorText = 'Authentication failed. Please check your API configuration.';
          } else if (error.message.includes('timeout')) {
            errorText = 'The request timed out. Please try again.';
          } else {
            errorText = `Sorry, I encountered an error: ${error.message}`;
          }
        }

        // Update the placeholder with error message
        const errorMessage: KodiMessage = {
          id: messageId,
          query: userQuery,
          response: {
            summary: errorText,
            isThinking: false
          },
          timestamp: new Date()
        };

        // Replace the placeholder message with the error (only if still mounted)
        if (!isMountedRef.current) return;

        setMessages((prevMessages) =>
          prevMessages.map((msg) => (msg.id === messageId ? errorMessage : msg))
        );

        // Show error toast if user is not on the assistant page
        const isOnAssistantPageError = currentPathRef.current.includes('/assistant');
        if (!isOnAssistantPageError) {
          const toastId = addToast({
            type: 'error',
            title: 'KODI Error',
            message: errorText,
            duration: 8000,
            onClick: () => {
              removeToast(toastId);
              navigate('/v2/assistant');
            }
          });
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
          setIsProcessing(false);
        }
      }
    },
    [query, context, messages, addToast, removeToast, navigate]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    // Reset context dialog history
    if (context) {
      setContext({
        ...context,
        dialog_history: []
      });
    }
  }, [context]);

  return (
    <KodiContext.Provider
      value={{
        messages,
        isLoading,
        isProcessing,
        hasUnreadResponse,
        query,
        setQuery,
        sendQuery,
        clearMessages,
        playingClipId,
        handlePlayPause
      }}
    >
      {children}
    </KodiContext.Provider>
  );
};
